import { useEffect, useState, useMemo } from 'react';
import { FileWarning, Search, Clock, CheckCircle2, AlertCircle, FilterX, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../ui/Spinner';
import { PageHeader } from '../ui/PageHeader';
import { fmtDateLong } from '../../lib/datetime';

/**
 * Pestaña admin: vista global de TODAS las entregas BIM del campus.
 * Lectura solamente — el admin ve estado, alumno, curso, nota y fechas.
 * Para editar comentarios/notas, el docente sigue usando "Dashboard BIM".
 */
interface BimEntregaRow {
  id:                 string;
  entrega_num:        number;
  status:             'pendiente' | 'en_revision' | 'corregido';
  nota_final:         number | null;
  updated_at:         string;
  archivo_nombre:     string | null;
  alumno:  { id: string; full_name: string | null; email: string } | null;
  curso:   { id: string; name: string } | null;
  uploader:{ id: string; full_name: string | null } | null;
}

type StatusFilter = 'all' | 'pendiente' | 'en_revision' | 'corregido';

const STATUS_INFO = {
  pendiente:   { label: 'Pendiente',   chip: 'chip-rose',    icon: AlertCircle },
  en_revision: { label: 'En revisión', chip: 'chip-amber',   icon: Clock },
  corregido:   { label: 'Publicada',   chip: 'chip-emerald', icon: CheckCircle2 },
} as const;

export function BimEntregasPage() {
  const [rows, setRows]       = useState<BimEntregaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [statusF, setStatusF] = useState<StatusFilter>('all');

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('bim_entregas')
      .select(`
        id, entrega_num, status, nota_final, updated_at, archivo_nombre,
        alumno:alumno_id (id, full_name, email),
        curso:curso_id (id, name),
        uploader:uploaded_by (id, full_name)
      `)
      .order('updated_at', { ascending: false });
    if (error) {
      console.error('[BimEntregasPage] error:', error);
      setRows([]);
    } else {
      setRows((data ?? []) as any);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return rows.filter(r => {
      if (statusF !== 'all' && r.status !== statusF) return false;
      if (!term) return true;
      const haystack = [
        r.alumno?.full_name, r.alumno?.email,
        r.curso?.name, r.archivo_nombre,
        `entrega ${r.entrega_num}`,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search, statusF]);

  const counts = useMemo(() => {
    const c = { all: rows.length, pendiente: 0, en_revision: 0, corregido: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Análisis"
        eyebrowIcon={FileWarning}
        title="Entregas BIM"
        description="Vista global de todas las entregas BIM del campus — estado, nota y docente que las cargó."
        actions={
          <button
            onClick={load}
            disabled={loading}
            className="btn-glass"
            title="Recargar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Recargar</span>
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Total" value={counts.all} accent="text-white" />
        <StatPill label="Pendientes" value={counts.pendiente} accent="text-rose-400" />
        <StatPill label="En revisión" value={counts.en_revision} accent="text-amber-400" />
        <StatPill label="Publicadas"  value={counts.corregido}  accent="text-cubus-lime" />
      </div>

      {/* Filtros */}
      <section className="glass-surface rounded-2xl p-4 border border-white/[0.06]">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="search"
              placeholder="Buscar por alumno, curso, archivo…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-cubus-deep border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cubus-lime/40"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'pendiente', 'en_revision', 'corregido'] as StatusFilter[]).map(s => {
              const isActive = statusF === s;
              const label = s === 'all' ? 'Todas' : STATUS_INFO[s].label;
              return (
                <button
                  key={s}
                  onClick={() => setStatusF(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-cubus-lime/20 text-cubus-lime border border-cubus-lime/40'
                      : 'bg-white/[0.04] text-white/60 border border-white/10 hover:bg-white/[0.08]'
                  }`}
                >
                  {label} <span className="opacity-60">({counts[s]})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tabla */}
      <section className="glass-surface rounded-2xl border border-white/[0.06] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50">
            <FilterX className="w-10 h-10 mx-auto mb-3 text-white/30" />
            <p className="text-sm">
              {rows.length === 0 ? 'Aún no hay entregas BIM cargadas.' : 'No hay entregas que coincidan con el filtro.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-white/40">
                  <th className="text-left py-3 px-4">Alumno</th>
                  <th className="text-left py-3 px-4">Curso</th>
                  <th className="text-center py-3 px-4">Entrega</th>
                  <th className="text-left py-3 px-4">Estado</th>
                  <th className="text-center py-3 px-4">Nota</th>
                  <th className="text-left py-3 px-4">Última actualización</th>
                  <th className="text-left py-3 px-4">Cargada por</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const sInfo = STATUS_INFO[r.status];
                  const Icon  = sInfo.icon;
                  return (
                    <tr key={r.id} className="border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors text-sm">
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">{r.alumno?.full_name || '—'}</div>
                        {r.alumno?.email && (
                          <div className="text-[11px] text-white/40">{r.alumno.email}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-white/80">{r.curso?.name || <span className="text-white/30 italic">sin curso</span>}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cubus-lime/15 text-cubus-lime font-bold text-xs border border-cubus-lime/30">
                          {r.entrega_num}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`chip ${sInfo.chip} inline-flex items-center gap-1`}>
                          <Icon size={11} />
                          {sInfo.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {r.nota_final != null ? (
                          <span className={`font-bold ${
                            r.nota_final >= 5.5 ? 'text-cubus-lime'
                            : r.nota_final >= 4.0 ? 'text-amber-400'
                            : 'text-rose-400'
                          }`}>{r.nota_final.toFixed(1)}</span>
                        ) : (
                          <span className="text-white/30">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-white/70 text-xs">{fmtDateLong(r.updated_at)}</td>
                      <td className="py-3 px-4 text-white/60 text-xs">{r.uploader?.full_name || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatPill({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="glass-surface rounded-xl border border-white/[0.06] px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</p>
      <p className={`text-display text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}
